import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import BackButton from "../components/BackButton";
import BackToTop from "../components/BackToTop";
import hljs from "../vendor/js/hljs-setup";
import { useEffect } from "react";


export default function ReactNativeExpo() {
    useEffect(() => {
        hljs.highlightAll();
    }, []);

    const nameExt = " | charlie-gp";
    const [postTitle, postDate, readTime] = [
        "React Native & Expo: The Essentials",
        "Jan. 30, 2026",
        "5min. read",
    ];

    return (
        <div id="posts">
            <Helmet>
                <title>{postTitle + nameExt}</title>
            </Helmet>
            <BackButton />

            <div id="post-date">
                <small>
                    {postDate} &nbsp;&bull;&nbsp; {readTime} &nbsp;&bull;&nbsp;{" "}
                    <Link to="/">all posts</Link>
                </small>
            </div>

            <h1 id="post-title">{postTitle}</h1>

            <p>
                React Native combined with Expo provides a robust environment for cross-platform mobile development. This post serves as a technical reference for essential commands, local build processes, and device configuration.
            </p>

            <h2 className="post-sect">Core Expo Commands</h2>
            <p>
                The Expo CLI is the primary interface for managing your project. Beyond the basics, these commands are essential for troubleshooting and daily development:
            </p>
            <pre>
                <code className="language-bash">
                    {`# Start the development server
npx expo start

# Start and clear the cache (Fixes many mysterious build issues)
npx expo start --clear

# Run on specific platforms directly
npx expo start --android
npx expo start --ios
npx expo start --web`}
                </code>
            </pre>

            <h2 className="post-sect">Dependency Management</h2>
            <p>
                Always use the Expo CLI to install libraries. It ensures that the version of the library you are installing is compatible with your current Expo SDK version.
            </p>
            <pre>
                <code className="language-bash">
                    {`# Correct way to install dependencies
npx expo install react-native-reanimated`}
                </code>
            </pre>

            <h2 className="post-sect">Local Builds with EAS</h2>
            <p>
                While EAS Cloud is convenient, local builds are often faster for debugging or when working within free tier limits. Requires <code>eas-cli</code> and Docker (for Android) or macOS (for iOS).
            </p>
            <pre>
                <code className="language-bash">
                    {`# Build Android APK locally
eas build --local --platform android --profile development

# Build iOS archive locally (macOS only)
eas build --local --platform ios --profile development`}
                </code>
            </pre>

            <h2 className="post-sect">Device Configuration</h2>
            <h3 className="post-sect-sub">Android Emulator</h3>
            <p>
                Ensure you have <strong>Android Studio</strong> installed with a configured AVD (Android Virtual Device). Start it from the CLI:
            </p>
            <pre>
                <code className="language-bash">
                    {`# List available emulators
emulator -list-avds

# Start a specific emulator
emulator -avd Pixel_7_API_33`}
                </code>
            </pre>

            <h3 className="post-sect-sub">Real Android Phone</h3>
            <ol className="lead">
                <li>Enable <strong>Developer Options</strong> on your phone (Tap "Build Number" 7 times).</li>
                <li>Enable <strong>USB Debugging</strong>.</li>
                <li>Connect via USB and ensure <code>adb devices</code> lists your device.</li>
                <li>Run <code>npx expo start</code> and scan the QR code using the <strong>Expo Go</strong> app.</li>
            </ol>

            <h2 className="post-sect">FAQ & Troubleshooting</h2>
            <div className="content-block">
                <h4 className="fw-bold">Q: Changes aren't reflecting in the app?</h4>
                <p>A: Try pressing <strong>r</strong> in the terminal to reload, or use <code>npx expo start --clear</code>.</p>

                <h4 className="fw-bold">Q: "Network request failed" when connecting real device or emulator?</h4>
                <p>A: For real devices, ensure both computer and phone are on the same Wi-Fi. For <strong>Android Emulators</strong>, if you are trying to connect to a local backend, use the IP <code>10.0.2.2</code> instead of <code>localhost</code> or <code>127.0.0.1</code>, as the emulator sees itself as localhost.</p>

                <h4 className="fw-bold">Q: How to open the Developer Menu on device?</h4>
                <p>A: Shake your phone or use <code>adb shell input keyevent 82</code> for Android.</p>
            </div>

            <BackToTop />

            <div className="mt-5">
                <BackButton />
            </div>
        </div>
    );
}
